import { useForm, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../../components/AdminLayout'
import { UploadField } from '../../../components/admin/UploadField'

interface ProductImage {
  id: number
  url: string
}

interface Product {
  id: number
  name: string
  description: string
  price: number
  priceRub: number | null
  priceByn: number | null
  stock: number
  productType: string
  /** One of Product::CATEGORIES, or null for a piece off the landing rows. */
  category: string | null
  customizable: boolean
  listed: boolean
  stepCount: number
  images: ProductImage[]
}

interface Props {
  product: Product
}

export default function AdminProductsEdit({ product }: Props) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [imagesToRemove, setImagesToRemove] = useState<number[]>([])
  const { data, setData, processing } = useForm({
    name: product.name,
    description: product.description || '',
    price: product.price.toString(),
    // Fixed tags; empty means "convert the dollars at the day's rate".
    price_rub: product.priceRub?.toString() ?? '',
    price_byn: product.priceByn?.toString() ?? '',
    stock: product.stock.toString(),
    // Selects hand back strings; the server casts them.
    product_type: product.productType,
    category: product.category ?? '',
    customizable: product.customizable ? 'true' : 'false',
    listed: product.listed ? 'true' : 'false',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const formData = new FormData()
    formData.append('name', data.name)
    formData.append('description', data.description)
    formData.append('price', data.price)
    formData.append('price_rub', data.price_rub)
    formData.append('price_byn', data.price_byn)
    formData.append('stock', data.stock)
    formData.append('product_type', data.product_type)
    formData.append('category', data.category)
    formData.append('customizable', data.customizable)
    formData.append('listed', data.listed)
    selectedFiles.forEach((file) => formData.append('images[]', file))
    imagesToRemove.forEach((id) => formData.append('remove_image_ids[]', id.toString()))

    router.patch(`/admin/products/${product.id}`, formData, { forceFormData: true })
  }

  const toggleRemove = (imageId: number) => {
    setImagesToRemove((prev) =>
      prev.includes(imageId) ? prev.filter((id) => id !== imageId) : [...prev, imageId]
    )
  }

  return (
    <AdminLayout>
      <Link href="/admin/products" className="sss-admin-back">
        ← Back to products
      </Link>

      <div className="sss-admin-head">
        <p className="sss-admin-eyebrow sss-admin-eyebrow--accent">Catalog</p>
        <h1 className="sss-admin-title">Edit product</h1>
      </div>

      <div className="sss-admin-card" style={{ maxWidth: 720 }}>
        <div className="sss-admin-card__body">
          <form onSubmit={handleSubmit} className="sss-admin-form">
            <div className="sss-admin-field">
              <label htmlFor="name" className="sss-admin-label">
                Product name *
              </label>
              <input
                id="name"
                type="text"
                className="sss-admin-input"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                required
              />
            </div>

            <div className="sss-admin-field">
              <label htmlFor="description" className="sss-admin-label">
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                className="sss-admin-textarea"
                value={data.description}
                onChange={(e) => setData('description', e.target.value)}
              />
            </div>

            <div className="sss-admin-grid-2">
              <div className="sss-admin-field">
                <label htmlFor="price" className="sss-admin-label">
                  Price *
                </label>
                <div className="sss-admin-price-wrap">
                  <span className="sss-admin-price-wrap__sign">$</span>
                  <input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    className="sss-admin-input sss-admin-input--price"
                    value={data.price}
                    onChange={(e) => setData('price', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="sss-admin-field">
                <label htmlFor="stock" className="sss-admin-label">
                  Stock *
                </label>
                <input
                  id="stock"
                  type="number"
                  min="0"
                  className="sss-admin-input"
                  value={data.stock}
                  onChange={(e) => setData('stock', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* BYN is the base for the public market tags; RUB remains a legacy admin field. */}
            <div className="sss-admin-grid-2">
              {(
                [
                  ['price_rub', '₽', 'Price in RUB'],
                  ['price_byn', 'Byn', 'Price in BYN'],
                ] as const
              ).map(([key, sign, label]) => (
                <div key={key} className="sss-admin-field">
                  <label htmlFor={key} className="sss-admin-label">
                    {label}
                  </label>
                  <div className="sss-admin-price-wrap">
                    <span className="sss-admin-price-wrap__sign">{sign}</span>
                    <input
                      id={key}
                      type="number"
                      step="1"
                      min="0"
                      placeholder="convert from $"
                      className="sss-admin-input sss-admin-input--price"
                      value={data[key]}
                      onChange={(e) => setData(key, e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="sss-admin-grid-2">
              <div className="sss-admin-field">
                <label htmlFor="category" className="sss-admin-label">
                  Category
                </label>
                <select
                  id="category"
                  className="sss-admin-select"
                  value={data.category}
                  onChange={(e) => setData('category', e.target.value)}
                >
                  <option value="">No category — off the landing rows</option>
                  <option value="apple">Apple</option>
                  <option value="lighter_cases">Lighter cases</option>
                  <option value="vape_cases">Vape cases</option>
                  <option value="airpods_pro_cases">AirPods Pro cases</option>
                </select>
              </div>
              <div className="sss-admin-field">
                <label htmlFor="product_type" className="sss-admin-label">
                  Product type
                </label>
                <select
                  id="product_type"
                  className="sss-admin-select"
                  value={data.product_type}
                  onChange={(e) => setData('product_type', e.target.value)}
                >
                  <option value="one_of_one">1 of 1 — a single piece</option>
                  <option value="mass_produced">Mass produced — made in batches</option>
                  <option value="custom">
                    Custom — made to order, design agreed after payment
                  </option>
                </select>
              </div>
              <div className="sss-admin-field">
                <label htmlFor="listed" className="sss-admin-label">
                  Visibility
                </label>
                <select
                  id="listed"
                  className="sss-admin-select"
                  value={data.listed}
                  onChange={(e) => setData('listed', e.target.value)}
                >
                  <option value="true">Listed — shown in the shop</option>
                  <option value="false">Unlisted — hidden, kept on past orders</option>
                </select>
              </div>
            </div>

            <div className="sss-admin-field">
              <label htmlFor="customizable" className="sss-admin-label">
                Customization
              </label>
              <select
                id="customizable"
                className="sss-admin-select"
                value={data.customizable}
                onChange={(e) => setData('customizable', e.target.value)}
              >
                <option value="false">None — buy as shown</option>
                <option value="true">Made to order — runs through the configurator</option>
              </select>
              <p className="sss-admin-hint">
                {data.customizable === 'true'
                  ? `${product.stepCount} configurator step(s) defined.`
                  : 'Turn this on to give the piece a step-by-step build.'}
              </p>
            </div>

            {product.images.length > 0 && (
              <div className="sss-admin-field">
                <span className="sss-admin-label">Current images</span>
                <div className="sss-admin-thumbs">
                  {product.images.map((image) => {
                    const removing = imagesToRemove.includes(image.id)
                    return (
                      <div
                        key={image.id}
                        className={`sss-admin-thumb ${removing ? 'is-removing' : ''}`}
                      >
                        <img src={image.url} alt="Product" />
                        <button
                          type="button"
                          onClick={() => toggleRemove(image.id)}
                          className={`sss-admin-thumb__btn ${removing ? 'sss-admin-thumb__btn--restore' : ''}`}
                          title={removing ? 'Restore' : 'Remove'}
                        >
                          {removing ? '↺' : '×'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <UploadField
              label="Add more images"
              files={selectedFiles}
              onAdd={(files) => setSelectedFiles([...selectedFiles, ...files])}
              onRemove={(index) => setSelectedFiles(selectedFiles.filter((_, i) => i !== index))}
            />

            <div className="sss-admin-actions">
              <button
                type="submit"
                className="sss-admin-btn sss-admin-btn--accent"
                disabled={processing}
              >
                {processing ? 'Saving…' : 'Save changes'}
              </button>
              {product.customizable && (
                <Link
                  href={`/admin/products/${product.id}/customization`}
                  className="sss-admin-btn sss-admin-btn--ghost"
                >
                  Edit configurator →
                </Link>
              )}
              <Link href="/admin/products" className="sss-admin-btn sss-admin-btn--ghost">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  )
}
